import { Component } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { QueryParamsService } from 'src/app/shared/utils/query-params.service';
import {
  ScoreInfo,
  getScoresInfo,
} from 'src/app/shared/utils/score-type-details';
import { ScorelabSetupComponent } from 'src/app/project/settings/scorelab-setup/scorelab-setup.component';

/** Shown when the active score id is unlicensed, stale or unset. */
const FALLBACK_SCORE_ID = 'sentiment';

/**
 * Nav-bar button showing the score currently in play and opening the existing
 * Scorelab dialog. A focused `?score=` param wins over the saved default, the
 * same precedence `chart-title` uses.
 *
 * Emits the whole `ScoreInfo` because the compact nav layout renders `short`
 * ("GR") where the full-width one renders `name` ("General Risk").
 */
@Component({
  selector: 'app-score-button',
  templateUrl: './score-button.component.html',
  styleUrls: ['./score-button.component.scss'],
})
export class ScoreButtonComponent {
  readonly score$: Observable<ScoreInfo>;

  constructor(
    private dialog: MatDialog,
    private featureFlagsService: FeatureFlagsService,
    queryParamsService: QueryParamsService,
  ) {
    this.score$ = combineLatest([
      queryParamsService.queryParams$,
      this.featureFlagsService.scorelabSettings$,
    ]).pipe(
      map(([params, scorelabSettings]) => {
        const id = params?.score || scorelabSettings?.defaultScore;
        const info = id
          ? (getScoresInfo(scorelabSettings, id) as ScoreInfo)
          : null;
        // getScoresInfo yields an empty name for an unlicensed or stale id.
        return info?.name
          ? info
          : (getScoresInfo(scorelabSettings, FALLBACK_SCORE_ID) as ScoreInfo);
      }),
      distinctUntilChanged((a, b) => a.name === b.name),
    );
  }

  openScoreSetup(): void {
    this.dialog.open(ScorelabSetupComponent, { autoFocus: false });
  }
}
